import { useMutation } from '@tanstack/react-query';
import { isEmpty } from 'lodash';
import { type RefObject, useMemo, useState } from 'react';
import { useSnapshot } from 'valtio';
import {
  generateEvidenceEmailChain as apiEvidenceChain,
  generateRecordsResearchReport as apiGenerateReport,
} from '~/api/projects2';
import { notifyExportQueued } from '~/common/notifyExportQueued';
import {
  capturePosthogEvent,
  captureTypedPosthogEvent,
  POSTHOG_EVENTS,
  type UtilitiesReportsSource,
} from '~/common/posthogEvents';
import { RequestStatus } from '~/common/types';
import Notify from '~/components2/Notify';
import { SendEmailModal } from '~/components2/SendEmailModal';
import store from '../store';
import { buildOutreachLogRows, downloadCSV, generateCSV } from './csv';

const invalidStatuses = [RequestStatus.Cancelled, RequestStatus.Dismissed, RequestStatus.Pending];

/**
 * Utilities report actions, shared by the Reports subtab and the Exports menu. Render `modals` wherever they're used.
 * `source` tags the PostHog events; `returnFocusRef` takes focus when a modal closes.
 */
export function useReportExports(
  projectId: string,
  source: UtilitiesReportsSource,
  returnFocusRef?: RefObject<HTMLElement | null>,
) {
  const { agencies, project, dataRequests } = useSnapshot(store) as typeof store;

  const [reportConfirmOpen, setReportConfirmOpen] = useState(false);
  const [communicationLogConfirmOpen, setCommunicationLogConfirmOpen] = useState(false);

  const mutation = useMutation({
    mutationKey: ['generate-records-research-report', { projectId }],
    mutationFn: ({ projectId, ccEmails }: { projectId: string; ccEmails?: string[] }) =>
      apiGenerateReport(projectId, ccEmails),
    onSuccess: (_data, { ccEmails }) => {
      captureTypedPosthogEvent(POSTHOG_EVENTS.civilsearchRecordsResearchReportGenerated, {
        project_id: projectId,
        source,
      });

      notifyExportQueued({
        message: 'The records research report will be emailed to you in a few minutes.',
        ccEmails,
      });
    },
    onError: (e) => {
      console.error('Error generating records research report: ', e);
      Notify.error({
        message: 'An error occurred while generating your report, please contact support.',
      });
    },
  });

  const sendCommunicationLog = async (ccEmails: string[] | undefined) => {
    const title = 'Agency Communication Log';
    try {
      const res = await apiEvidenceChain(projectId, ccEmails);
      if (!res.success) throw new Error('Bad Response');

      notifyExportQueued({
        title,
        message: 'The agency communication log will be emailed to you in a few minutes.',
        ccEmails,
      });

      captureTypedPosthogEvent(POSTHOG_EVENTS.civilsearchAgencyCommunicationLogGenerated, {
        project_id: projectId,
        source,
        request_ids: res.requestIds,
      });
    } catch (e) {
      console.error(`Error generating agency communication log: `, e);
      Notify.error({
        title,
        message: 'An error occurred while generating your agency communication log. Please contact support.',
      });
      return false;
    }
  };

  const exportOutreachLog = () => {
    const rows = buildOutreachLogRows(Object.values(agencies), Object.values(store.dataRequests));

    const csv = generateCSV(rows);
    downloadCSV(csv, project?.name || 'Project');

    capturePosthogEvent(POSTHOG_EVENTS.civilsearchOutreachLogExported, {
      project_id: projectId,
      source,
    });
  };

  // The communication log has nothing to send until a request has started.
  const hasActiveRequest = useMemo<boolean>(() => {
    if (isEmpty(dataRequests)) return false;
    return Object.values(dataRequests).some((r) => !invalidStatuses.includes(r.requestStatus));
  }, [dataRequests]);

  const onCloseAutoFocus = returnFocusRef
    ? (event: Event) => {
        event.preventDefault();
        returnFocusRef.current?.focus();
      }
    : undefined;

  const modals = (
    <>
      <SendEmailModal
        open={reportConfirmOpen}
        onOpenChange={setReportConfirmOpen}
        title="Records Research Report"
        testIdPrefix="utilities-records-research-report"
        onCloseAutoFocus={onCloseAutoFocus}
        onSend={(ccEmails) => mutation.mutateAsync({ projectId, ccEmails: ccEmails.length > 0 ? ccEmails : undefined })}
      />
      <SendEmailModal
        open={communicationLogConfirmOpen}
        onOpenChange={setCommunicationLogConfirmOpen}
        title="Agency Communication Log"
        testIdPrefix="utilities-agency-communication-log"
        onCloseAutoFocus={onCloseAutoFocus}
        onSend={(ccEmails) => sendCommunicationLog(ccEmails.length > 0 ? ccEmails : undefined)}
      />
    </>
  );

  return {
    hasActiveRequest,
    exportOutreachLog,
    openRecordsResearchReport: () => setReportConfirmOpen(true),
    openCommunicationLog: () => setCommunicationLogConfirmOpen(true),
    modals,
  };
}
